import { type LatestQueueItem, type OptJobType, useLatestQueueItem } from 'api/optJobs/optJobs.api'
import { useEffect, useState } from 'react'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Datetime from 'utils/datetime/datetime'

export function useTimeSincePublished({ latestQueueItem }: { latestQueueItem: LatestQueueItem | null }): string | null {
  const [timeSincePublished, setTimeSincePublished] = useState<string | null>(null)

  useEffect(() => {
    const updatedAt = latestQueueItem?.updated_at

    const calculateTimeSince = () => {
      if (!updatedAt) {
        setTimeSincePublished(null)
        return
      }

      setTimeSincePublished(
        Datetime.getDurationInHoursAndMinutes(
          Datetime.toISOString(updatedAt),
          Datetime.toISOString(Datetime.getISONow(0, false))
        )
      )
    }

    calculateTimeSince()
    const interval = setInterval(calculateTimeSince, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [latestQueueItem])

  return timeSincePublished
}

export function getAllowedOptJobSubtypes({
  optJobType,
  user,
  activeSystem,
}: {
  optJobType: OptJobType
  user: User | null
  activeSystem: System | null
}): Subtype[] {
  let subtypes: Subtype[] = []

  if (!activeSystem || !user) {
    return subtypes
  }

  subtypes = activeSystem.subtypes?.[optJobType] ?? []

  // If collaborator, only show allowed subtypes
  if (user.collaborator_context?.optjob_allowance?.length) {
    const allowedSubtypes = new Set(
      user.collaborator_context.optjob_allowance
        .filter(({ opt_job_type }) => opt_job_type === optJobType)
        .map(({ opt_job_subtype }) => opt_job_subtype)
    )

    subtypes = subtypes.filter((st) => allowedSubtypes.has(st.subtype))
  }

  return subtypes
}

export function useLatestQueueItemsInfo({ type }: { type: OptJobType }) {
  const { user, activeSystem } = useAuth()

  const subtypes = getAllowedOptJobSubtypes({
    optJobType: type,
    user,
    activeSystem,
  })

  const { data: latestQueueItemResponse, status: latestQueueItemStatus } = useLatestQueueItem({
    type: type,
    systemId: activeSystem?.id ?? 0,
    queueItemStatus: 'Finished',
  })

  return {
    subtypes,
    latestQueueItemData: latestQueueItemResponse ?? null,
    status: latestQueueItemStatus,
  }
}
